import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import qs from 'qs'
import { fromPromise } from 'xstate'

import {
  AppointmentType,
  LanguageResult,
  NodeAutocompleteResult,
  PatientAgeGroup,
  PublicNode,
  SupportedLanguage,
} from '../__generated__/api'
import { AppointmentDuration } from '../common/components/AppointmentLengthSelect/AppointmentLengthSelect'
import { FilterOptions, Gender } from '../common/components/FilterOptions/types'
import { SearchTreeItem } from '../common/components/Search/types'
import {
  APPOINTMENT_PARAMETERS,
  COOKIE_PARAMETERS,
  GLOBAL_PARAMETERS,
  SEARCH_PARAMETERS,
} from '../common/constants/urlParameters'
import i18n from '../common/i18n/i18n'
import api from '../common/services/api'
import { fetchEhoitoEvents } from '../common/services/omApiFetch'
import { AppointmentSearchMode, defaultDentalSearchNodeId } from '../state/search/atoms'

import { ParsedURL, ResolvedService } from './types'

const getNode = async (
  nodeId: string,
  lang: SupportedLanguage,
  isOhc: boolean
): Promise<PublicNode | undefined> => {
  try {
    const res = await api.v1.searchNode({
      nodeId,
      lang,
      isOhc,
    })
    return res.data[0]
  } catch {
    return undefined
  }
}

const getParentNode = async (
  childNode: PublicNode | undefined,
  lang: SupportedLanguage,
  isOhc: boolean
): Promise<PublicNode | undefined> => {
  if (!childNode || !childNode.parentId) {
    return undefined
  }

  return getNode(childNode.parentId, lang, isOhc)
}

export const parseURL = fromPromise(async (): Promise<ParsedURL> => {
  const pathname = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)

  const isDental =
    searchParams.has(GLOBAL_PARAMETERS.NO_LANDING) &&
    searchParams.get(GLOBAL_PARAMETERS.NO_LANDING) === 'dental'
  const isOHC = pathname.includes('tyoterveys') || pathname.includes('tep')
  const lang =
    searchParams.has(GLOBAL_PARAMETERS.LANG) &&
    Object.values(SupportedLanguage).includes(
      searchParams.get(GLOBAL_PARAMETERS.LANG) as SupportedLanguage
    )
      ? (searchParams.get(GLOBAL_PARAMETERS.LANG) as SupportedLanguage)
      : SupportedLanguage.Fi

  await i18n.changeLanguage(lang)

  let nodeData = {}
  if (searchParams.has(SEARCH_PARAMETERS.NODE_ID)) {
    const node = await getNode(searchParams.get(SEARCH_PARAMETERS.NODE_ID) as string, lang, isOHC)
    const parentNode = await getParentNode(node, lang, isOHC)
    nodeData = {
      nodeId: searchParams.get(SEARCH_PARAMETERS.NODE_ID) as string,
      node,
      parentNode,
    }
  } else if (isDental) {
    const node = await getNode(defaultDentalSearchNodeId, lang, isOHC)
    const parentNode = await getParentNode(node, lang, isOHC)
    nodeData = {
      nodeId: defaultDentalSearchNodeId,
      node,
      parentNode,
    }
  }

  let practitionerData = {}
  if (searchParams.has(SEARCH_PARAMETERS.PRACTITIONER_ID)) {
    const practitionerId = Number(searchParams.get(SEARCH_PARAMETERS.PRACTITIONER_ID))
    practitionerData = { practitionerId }

    if (searchParams.has(SEARCH_PARAMETERS.REASON)) {
      const standardScheduleOptionString = searchParams.get(SEARCH_PARAMETERS.REASON)!
      const standardScheduleOption = isNaN(parseInt(standardScheduleOptionString as string))
        ? (standardScheduleOptionString as AppointmentDuration)
        : (parseInt(standardScheduleOptionString as string) as AppointmentDuration)

      practitionerData = {
        ...practitionerData,
        preselectedStandardScheduleOption: standardScheduleOption,
      }
    }
  }

  // FIXME:
  // - useNodeLinkings logic?

  const parsed = qs.parse(searchParams.toString(), { arrayLimit: 1000 })

  // Search filter options
  const filterOptions: Partial<FilterOptions> = {}

  if (parsed[SEARCH_PARAMETERS.APPOINTMENT_TYPE]) {
    filterOptions.appointmentTypes = parsed[SEARCH_PARAMETERS.APPOINTMENT_TYPE] as AppointmentType[]
  }
  if (parsed[SEARCH_PARAMETERS.TIME_RANGE]) {
    const timeRange = parsed[SEARCH_PARAMETERS.TIME_RANGE] as { start: string; end: string }
    if (timeRange.start && timeRange.end) {
      filterOptions.timeRange = {
        start: parseInt(timeRange.start),
        end: parseInt(timeRange.end),
      }
    }
  }
  if (parsed[SEARCH_PARAMETERS.GENDER]) {
    filterOptions.gender = parsed[SEARCH_PARAMETERS.GENDER] as Gender
  }
  if (parsed[SEARCH_PARAMETERS.LANGUAGE]) {
    filterOptions.language = parsed[SEARCH_PARAMETERS.LANGUAGE] as unknown as LanguageResult
  }
  if (parsed[SEARCH_PARAMETERS.PATIENT_AGE_GROUP_OLD]) {
    const _ageGroups = parsed[SEARCH_PARAMETERS.PATIENT_AGE_GROUP_OLD] as unknown as PatientAgeGroup
    filterOptions.patientAgeGroup = { ..._ageGroups, id: Number(_ageGroups.id) }
  }

  const isFromApp =
    searchParams.has(GLOBAL_PARAMETERS.IS_FROM_APP) || Cookies.get(COOKIE_PARAMETERS.MOBILE) === '1'

  const hasInsurance =
    searchParams.has(SEARCH_PARAMETERS.INSURANCE) ||
    (searchParams.has(GLOBAL_PARAMETERS.NO_LANDING) &&
      searchParams.get(GLOBAL_PARAMETERS.NO_LANDING) === 'insurance')

  const noServiceSelect = searchParams.has(SEARCH_PARAMETERS.NO_SERVICE_SELECT)

  return {
    isOHC,
    isDental,
    filterOptions,
    ...nodeData,
    ...practitionerData,
    ...(searchParams.has(SEARCH_PARAMETERS.DATE)
      ? { date: dayjs(searchParams.get(SEARCH_PARAMETERS.DATE)) }
      : {}),
    ...(searchParams.has(APPOINTMENT_PARAMETERS.MODE_OLD)
      ? {
          appointmentSearchMode: searchParams.get(
            APPOINTMENT_PARAMETERS.MODE_OLD
          ) as AppointmentSearchMode,
        }
      : {}),
    ...(searchParams.has(APPOINTMENT_PARAMETERS.MODE)
      ? {
          appointmentSearchMode: searchParams.get(
            APPOINTMENT_PARAMETERS.MODE
          ) as AppointmentSearchMode,
        }
      : {}),
    ...(parsed.lo ? { location: parsed.lo as string[] } : {}),
    noLanding: searchParams.has(GLOBAL_PARAMETERS.NO_LANDING),
    callbackOpen: searchParams.has(APPOINTMENT_PARAMETERS.CALLBACK_OPEN),
    isFromApp,
    hasInsurance,
    noServiceSelect,
    pathname,
    ...(searchParams.has(SEARCH_PARAMETERS.MODAL)
      ? { noteModal: searchParams.get(SEARCH_PARAMETERS.MODAL) as string }
      : {}),
    ...(searchParams.has(SEARCH_PARAMETERS.MODAL_ACTION)
      ? { modalAction: searchParams.get(SEARCH_PARAMETERS.MODAL_ACTION) as string }
      : {}),
    openServiceSelect: searchParams.has(SEARCH_PARAMETERS.SERVICE_SELECT),
  }
})

export const fetchNode = fromPromise(
  async ({
    input,
  }: {
    input: { lang: SupportedLanguage; nodeId: string; isOhc: boolean }
  }): Promise<PublicNode | undefined> => {
    return getNode(input.nodeId, input.lang, input.isOhc)
  }
)

export const resolveServiceSelection = fromPromise(
  async ({
    input,
  }: {
    input: {
      service: Array<SearchTreeItem<PublicNode>> | PublicNode | NodeAutocompleteResult
      lang: SupportedLanguage
      isOHC: boolean
    }
  }): Promise<ResolvedService> => {
    const { service, lang, isOHC } = input

    // Received breadcrumb
    if (Array.isArray(service)) {
      if (service.length === 0) {
        return undefined
      }
      return service[0].item
    }

    // Received node
    if ('id' in service) {
      return service
    }

    // Autocomplete handlers
    if (service.practitionerData) {
      return { type: 'practitioner', id: service.practitionerData.id }
    }

    if (service.searchKeywordData) {
      const res = await api.v1.searchNode({
        keywordId: service.searchKeywordData.id,
        lang,
        isOhc: isOHC,
        onlyListed: true,
      })
      return res.data[0]
    }

    if (service.nodeData) {
      const res = await api.v1.searchNode({
        nodeId: service.nodeData.id,
        lang,
        isOhc: isOHC,
        onlyListed: true,
      })
      return res.data[0]
    }

    if (service.groupData) {
      const res = await api.v1.searchNode({
        keyword: service.keyword,
        lang,
        isOhc: isOHC,
        onlyListed: true,
      })
      return res.data[0]
    }

    return undefined
  }
)

export const fetchPractitionerDetails = fromPromise(
  async ({
    input,
  }: {
    input: { lang: SupportedLanguage; practitionerId: number; isOHC: boolean }
  }) => {
    const res = await api.v1.getPractitionerDetails(input)
    return res.data
  }
)

export const fetchOMEvents = fromPromise(async () => {
  return await fetchEhoitoEvents()
})
